import { ProjectShape, useShapeService } from '@/api/services/shapeService';
import { Subproject } from '@/stores/SubprojectStore';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface SitePanelProps {
  shapes: ProjectShape[];
  selectedShapeId: string | null;
  projectName: string;
  projectId: string;
  subprojects: Subproject[];
  onSelectShape: (id: string | null) => void;
  onShapeUpdated: (shape: ProjectShape) => void;
  onDeleteShape: (shapeId: string) => void;
  onSelectThisSite: (shapeId: string) => void;
  onRemoveSubprojectAssignment: (subprojectId: string, shapeId: string) => void;
}

interface SiteRecordProps {
  shape: ProjectShape;
  projectId: string;
  shapeService: ReturnType<typeof useShapeService>;
  selected: boolean;
  subprojects: Subproject[];
  onSelect: () => void;
  onShapeUpdated: (shape: ProjectShape) => void;
  onDeleteShape: (shapeId: string) => void;
  onSelectThisSite: (shapeId: string) => void;
  onRemoveSubprojectAssignment: (subprojectId: string, shapeId: string) => void;
}

const SHAPE_COLOR_DEFAULT = '#00008B';

function ShapeTypeIcon({ geometryType, color }: { geometryType: string; color: string }) {
  const sx = { fontSize: '0.9rem', color, flexShrink: 0 };
  if (geometryType === 'LineString') return <TimelineIcon sx={sx} />;
  if (geometryType === 'Point') return <PlaceIcon sx={sx} />;
  return <HexagonOutlinedIcon sx={sx} />;
}

function SiteRecord({
  shape,
  projectId,
  shapeService,
  selected,
  subprojects,
  onSelect,
  onShapeUpdated,
  onDeleteShape,
  onSelectThisSite,
  onRemoveSubprojectAssignment,
}: SiteRecordProps) {
  const [areaUnit, setAreaUnit] = useState<'ft2' | 'm2'>('ft2');
  const [landCost, setLandCost] = useState<string>(
    shape.properties.landAcquisitionCost ?? '',
  );
  const [address, setAddress] = useState<string>(shape.properties.address ?? '');

  // Sync address when it changes externally (e.g. Nominatim fill)
  useEffect(() => {
    setAddress(shape.properties.address ?? '');
  }, [shape.properties.address]);

  const areaSqft = shape.properties.areaSqft;
  const areaDisplay =
    areaSqft != null
      ? areaUnit === 'ft2'
        ? `${areaSqft.toLocaleString()} ft²`
        : `${(areaSqft * 0.0929).toLocaleString(undefined, { maximumFractionDigits: 1 })} m²`
      : '—';

  // Subprojects assigned to this shape
  const assignedSubprojects = subprojects.filter((s) => s.projectShape === shape.id);

  const handleLandCostBlur = async () => {
    if (landCost === (shape.properties.landAcquisitionCost ?? '')) return;
    const val = landCost.trim() === '' ? null : parseFloat(landCost);
    if (val !== null && isNaN(val)) return; // non-numeric, non-empty: ignore
    try {
      const resp = await shapeService.update(projectId, shape.id, {
        properties: { landAcquisitionCost: val },
      });
      if (resp.data) onShapeUpdated(resp.data);
    } catch (e) {
      console.error('Failed to update land cost:', e);
    }
  };

  const handleAddressBlur = async () => {
    if (address === shape.properties.address) return;
    try {
      const resp = await shapeService.update(projectId, shape.id, {
        properties: { address },
      });
      if (resp.data) onShapeUpdated(resp.data);
    } catch (e) {
      console.error('Failed to update address:', e);
    }
  };

  return (
    <Accordion
      expanded={selected}
      onChange={onSelect}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'background.card',
        borderRadius: '8px !important',
        border: '1px solid',
        borderColor: selected ? 'info.main' : 'transparent',
        mb: 1.5,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />}
        sx={{
          px: 1.5,
          minHeight: '36px !important',
          bgcolor: 'rgba(255,255,255,0.08)',
          borderRadius: selected ? '7px 7px 0 0' : '7px',
          '& .MuiAccordionSummary-content': {
            margin: '6px 0',
            alignItems: 'center',
            gap: 0.75,
          },
        }}
      >
        <ShapeTypeIcon
          geometryType={shape.geometry.type}
          color={shape.properties.color || SHAPE_COLOR_DEFAULT}
        />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', flex: 1 }}>
          {shape.properties.name || 'Unnamed Site'}
        </Typography>
        {/* Colored dots for assigned subprojects */}
        {assignedSubprojects.map((sp) => (
          <Box
            key={sp.id}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: shape.properties?.color || SHAPE_COLOR_DEFAULT,
              flexShrink: 0,
            }}
          />
        ))}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteShape(shape.id);
          }}
          sx={{ color: 'text.secondary', p: 0.25, flexShrink: 0 }}
          aria-label="Delete site"
        >
          <DeleteIcon sx={{ fontSize: '0.9rem' }} />
        </IconButton>
      </AccordionSummary>

      <AccordionDetails
        sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {/* Assigned subprojects chips */}
        {assignedSubprojects.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {assignedSubprojects.map((sp) => (
              <Chip
                key={sp.id}
                label={sp.name}
                size="small"
                onDelete={() => onRemoveSubprojectAssignment(sp.id, shape.id)}
                sx={{
                  bgcolor: shape.properties?.color || SHAPE_COLOR_DEFAULT,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)' },
                }}
              />
            ))}
          </Box>
        )}

        {/* Land Acquisition Estimate */}
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            Land Acquisition Estimate
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="0"
            value={landCost}
            onChange={(e) => setLandCost(e.target.value)}
            onBlur={handleLandCostBlur}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
        </Box>

        {/* Total Area + unit toggle */}
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            Total Area
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
              {areaDisplay}
            </Typography>
            <ButtonGroup size="small" disableElevation>
              <Button
                variant={areaUnit === 'ft2' ? 'contained' : 'outlined'}
                onClick={() => setAreaUnit('ft2')}
                sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75, minWidth: 0 }}
              >
                ft²
              </Button>
              <Button
                variant={areaUnit === 'm2' ? 'contained' : 'outlined'}
                onClick={() => setAreaUnit('m2')}
                sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75, minWidth: 0 }}
              >
                m²
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        {/* Address */}
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            Address
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onBlur={handleAddressBlur}
          />
        </Box>

        {/* Lat / Lon (read-only) */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              Latitude
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={shape.properties.latitude?.toFixed(6) ?? '—'}
              InputProps={{ readOnly: true }}
              sx={{ '& input': { fontSize: '0.75rem' } }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              Longitude
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={shape.properties.longitude?.toFixed(6) ?? '—'}
              InputProps={{ readOnly: true }}
              sx={{ '& input': { fontSize: '0.75rem' } }}
            />
          </Box>
        </Box>

        {/* Select This Site */}
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => onSelectThisSite(shape.id)}
        >
          Select This Site
        </Button>
      </AccordionDetails>
    </Accordion>
  );
}

export function SitePanel({
  shapes,
  selectedShapeId,
  projectName,
  projectId,
  subprojects,
  onSelectShape,
  onShapeUpdated,
  onDeleteShape,
  onSelectThisSite,
  onRemoveSubprojectAssignment,
}: SitePanelProps) {
  const shapeService = useShapeService();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredShapes = searchQuery.trim()
    ? shapes.filter((s) =>
        s.properties.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : shapes;

  return (
    <Box
      sx={{
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            fontSize: '0.85rem',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Sites for {projectName}
        </Typography>
        <TextField
          size="small"
          placeholder="Search sites"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
        />
      </Box>

      {/* Site list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {shapes.length === 0 && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
          >
            Draw a polygon on the map to create a site.
          </Typography>
        )}
        {filteredShapes.map((shape) => (
          <SiteRecord
            key={shape.id}
            shape={shape}
            projectId={projectId}
            shapeService={shapeService}
            selected={selectedShapeId === shape.id}
            subprojects={subprojects}
            onSelect={() =>
              onSelectShape(selectedShapeId === shape.id ? null : shape.id)
            }
            onShapeUpdated={onShapeUpdated}
            onDeleteShape={onDeleteShape}
            onSelectThisSite={onSelectThisSite}
            onRemoveSubprojectAssignment={onRemoveSubprojectAssignment}
          />
        ))}
      </Box>
    </Box>
  );
}
